// src/components/admin/page-management/section-forms/NewsletterForm.js
import React, { useState } from 'react';
import ColorPicker from '../../shared/ColorPicker';
import MediaUploader from '../../shared/MediaUploader';
import { ChevronDown, ChevronUp, Eye, Settings, Palette, Type, Image, Layout } from 'lucide-react';

const NewsletterForm = ({ content, updateContent }) => {
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [activeTab, setActiveTab] = useState('content');
  
  const tabs = [
    { id: 'content', label: 'Content', icon: Type },
    { id: 'design', label: 'Design', icon: Palette },
    { id: 'layout', label: 'Layout', icon: Layout },
    { id: 'spacing', label: 'Spacing', icon: Image }
  ];

  const TabButton = ({ tab, isActive, onClick }) => {
    const Icon = tab.icon;
    return (
      <button
        type="button"
        onClick={() => onClick(tab.id)}
        className={`flex items-center px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
          isActive
            ? 'bg-blue-600 text-white shadow-md'
            : 'bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-800 border border-gray-200'
        }`}
      >
        <Icon className="h-4 w-4 mr-2" />
        {tab.label}
      </button>
    );
  };

  const FormSection = ({ title, children, description, icon: Icon }) => (
    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
      <div className="flex items-center mb-4">
        {Icon && <Icon className="h-5 w-5 text-blue-600 mr-3" />}
        <div>
          <h4 className="font-semibold text-gray-900">{title}</h4>
          {description && <p className="text-sm text-gray-500 mt-1">{description}</p>}
        </div>
      </div>
      {children}
    </div>
  );

  const InputField = ({ label, value, onChange, placeholder, type = "text", rows, description }) => (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      {type === 'textarea' ? (
        <textarea
          value={value}
          onChange={onChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
          rows={rows || 3}
          placeholder={placeholder}
        />
      ) : (
        <input
          type={type}
          value={value}
          onChange={onChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
          placeholder={placeholder}
        />
      )}
      {description && <p className="text-xs text-gray-500">{description}</p>}
    </div>
  );

  const ColorField = ({ label, color, onChange, description }) => (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      <div className="flex items-center space-x-3">
        <ColorPicker color={color} onChange={onChange} />
       
      </div>
    </div>
  );

  const CheckboxField = ({ label, checked, onChange, description }) => (
    <div className="flex items-start space-x-3">
      <div className="flex items-center h-5">
        <input
          type="checkbox"
          checked={checked}
          onChange={onChange}
          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
        />
      </div>
      <div className="flex-1">
        <label className="text-sm font-medium text-gray-700">{label}</label>
        {description && <p className="text-xs text-gray-500 mt-1">{description}</p>}
      </div>
    </div>
  );

  const renderContentTab = () => (
    <div className="space-y-6">
      <FormSection 
        title="Basic Content" 
        description="Configure the main text and messaging"
        icon={Type}
      >
        <div className="grid gap-6">
          <InputField
            label="Main Title"
            value={content?.title || 'Join Our Newsletter'}
            onChange={(e) => updateContent('title', e.target.value)}
            placeholder="Enter newsletter title"
          />
          
          <InputField
            label="Subtitle Description"
            value={content?.subtitle || 'Stay updated with our latest products, promotions, and packaging tips.'}
            onChange={(e) => updateContent('subtitle', e.target.value)}
            placeholder="Describe what subscribers will receive"
            type="textarea"
            rows={2}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InputField
              label="Subscribe Button Text"
              value={content?.buttonText || 'Subscribe'}
              onChange={(e) => updateContent('buttonText', e.target.value)}
              placeholder="Button text"
            />
            
            <InputField
              label="Email Input Placeholder"
              value={content?.placeholderText || 'Enter your email'}
              onChange={(e) => updateContent('placeholderText', e.target.value)}
              placeholder="Input placeholder text"
            />
          </div>

          <InputField
            label="Success Message"
            value={content?.successMessage || 'Thank you for subscribing!'}
            onChange={(e) => updateContent('successMessage', e.target.value)}
            placeholder="Message shown after successful subscription"
            description="This message appears when someone successfully subscribes"
          />
        </div>
      </FormSection>

      <FormSection 
        title="Privacy Settings" 
        description="Configure privacy text and compliance messaging"
      >
        <div className="space-y-4">
          <CheckboxField
            label="Show Privacy Text"
            checked={content?.showPrivacyText !== false}
            onChange={(e) => updateContent('showPrivacyText', e.target.checked)}
            description="Display privacy assurance message below the form"
          />
          
          {content?.showPrivacyText !== false && (
            <InputField
              label="Privacy Message"
              value={content?.privacyText || 'We respect your privacy and will never share your information.'}
              onChange={(e) => updateContent('privacyText', e.target.value)}
              placeholder="Privacy assurance message"
              type="textarea"
              rows={2}
            />
          )}
        </div>
      </FormSection>
    </div>
  );

  const renderDesignTab = () => (
    <div className="space-y-6">
      <FormSection 
        title="Background Style" 
        description="Choose how you want the background to appear"
        icon={Image}
      >
        <div className="space-y-6">
          <CheckboxField
            label="Use Background Image"
            checked={content?.hasBackgroundImage || false}
            onChange={(e) => updateContent('hasBackgroundImage', e.target.checked)}
            description="Upload a custom background image for the section"
          />
          
          {content?.hasBackgroundImage ? (
            <div className="space-y-4 pl-6 border-l-2 border-blue-200">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Background Image</label>
                <MediaUploader
                  type="image"
                  value={content?.backgroundImageUrl || ''}
                  onUploadComplete={(url) => updateContent('backgroundImageUrl', url)}
                  onRemove={() => updateContent('backgroundImageUrl', '')}
                />
              </div>
              
              <CheckboxField
                label="Add Color Overlay"
                checked={content?.overlayEnabled || false}
                onChange={(e) => updateContent('overlayEnabled', e.target.checked)}
                description="Add a color overlay on top of the background image"
              />
              
              {content?.overlayEnabled && (
                <ColorField
                  label="Overlay Color"
                  color={content?.overlayColor || 'rgba(0,0,0,0.5)'}
                  onChange={(color) => updateContent('overlayColor', color)}
                  description="Choose the overlay color and transparency"
                />
              )}
            </div>
          ) : (
            <div className="space-y-4 pl-6 border-l-2 border-gray-200">
              <CheckboxField
                label="Use Gradient Background"
                checked={content?.backgroundGradient || false}
                onChange={(e) => updateContent('backgroundGradient', e.target.checked)}
                description="Create a smooth color transition instead of solid color"
              />
              
              {content?.backgroundGradient ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <ColorField
                    label="Gradient Start Color"
                    color={content?.gradientFrom || '#f9fafb'}
                    onChange={(color) => updateContent('gradientFrom', color)}
                  />
                  <ColorField
                    label="Gradient End Color"
                    color={content?.gradientTo || '#f3f4f6'}
                    onChange={(color) => updateContent('gradientTo', color)}
                  />
                </div>
              ) : (
                <ColorField
                  label="Background Color"
                  color={content?.backgroundColor || '#f3f4f6'}
                  onChange={(color) => updateContent('backgroundColor', color)}
                  description="Solid background color for the section"
                />
              )}
            </div>
          )}
        </div>
      </FormSection>

      <FormSection 
        title="Text Colors" 
        description="Customize the appearance of all text elements"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <ColorField
            label="Main Title Color"
            color={content?.textColor || '#111827'}
            onChange={(color) => updateContent('textColor', color)}
          />
          <ColorField
            label="Subtitle Color"
            color={content?.subtitleColor || '#4b5563'}
            onChange={(color) => updateContent('subtitleColor', color)}
          />
        </div>
      </FormSection>

      <FormSection 
        title="Button Styling" 
        description="Customize the subscribe button appearance"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <ColorField
            label="Button Background"
            color={content?.buttonColor || '#013263'}
            onChange={(color) => updateContent('buttonColor', color)}
          />
          <ColorField
            label="Button Text Color"
            color={content?.buttonTextColor || '#ffffff'}
            onChange={(color) => updateContent('buttonTextColor', color)}
          />
        </div>
      </FormSection>

      <FormSection 
        title="Advanced Design Options" 
        description="Fine-tune colors and visual effects"
      >
        <button
          type="button"
          className="mb-6 inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
          onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
        >
          <Settings className="h-4 w-4 mr-2" />
          {showAdvancedSettings ? 'Hide Advanced Options' : 'Show Advanced Options'}
          {showAdvancedSettings ? (
            <ChevronUp className="h-4 w-4 ml-2" />
          ) : (
            <ChevronDown className="h-4 w-4 ml-2" />
          )}
        </button>
        
        {showAdvancedSettings && (
          <div className="space-y-6 pt-4 border-t border-gray-200">
            <div>
              <h5 className="font-medium text-gray-900 mb-4">Input Field Styling</h5>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <ColorField
                  label="Input Background"
                  color={content?.inputBackgroundColor || '#ffffff'}
                  onChange={(color) => updateContent('inputBackgroundColor', color)}
                />
                <ColorField
                  label="Input Text Color"
                  color={content?.inputTextColor || '#1f2937'}
                  onChange={(color) => updateContent('inputTextColor', color)}
                />
                <ColorField
                  label="Input Border Color"
                  color={content?.inputBorderColor || '#d1d5db'}
                  onChange={(color) => updateContent('inputBorderColor', color)}
                />
              </div>
            </div>
            
            <div>
              <h5 className="font-medium text-gray-900 mb-4">Visual Effects</h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <CheckboxField
                  label="Show Email Icon"
                  checked={content?.showIcon !== false}
                  onChange={(e) => updateContent('showIcon', e.target.checked)}
                  description="Display an email icon in the input field"
                />
                
                <CheckboxField
                  label="Button Hover Effect"
                  checked={content?.buttonHoverEffect !== false}
                  onChange={(e) => updateContent('buttonHoverEffect', e.target.checked)}
                  description="Add smooth hover animation to button"
                />
                
                <CheckboxField
                  label="Add Drop Shadow"
                  checked={content?.boxShadow || false}
                  onChange={(e) => updateContent('boxShadow', e.target.checked)}
                  description="Add subtle shadow around the form"
                />
                
                <CheckboxField
                  label="Show Title Divider"
                  checked={content?.showDivider !== false}
                  onChange={(e) => updateContent('showDivider', e.target.checked)}
                  description="Add decorative line below the title"
                />
              </div>
            </div>
            
            {content?.showIcon !== false && (
              <ColorField
                label="Email Icon Color"
                color={content?.iconColor || '#374151'}
                onChange={(color) => updateContent('iconColor', color)}
              />
            )}
            
            {content?.showDivider !== false && (
              <ColorField
                label="Divider Line Color"
                color={content?.dividerColor || '#e5e7eb'}
                onChange={(color) => updateContent('dividerColor', color)}
              />
            )}
          </div>
        )}
      </FormSection>
    </div>
  );

  const renderLayoutTab = () => (
    <div className="space-y-6">
      <FormSection 
        title="Form Layout" 
        description="Control the size and shape of form elements"
      >
        <div className="space-y-6">
          <InputField
            label="Maximum Form Width"
            value={content?.maxWidth || '36rem'}
            onChange={(e) => updateContent('maxWidth', e.target.value)}
            placeholder="e.g., 36rem, 576px, 100%"
            description="Maximum width of the subscription form container"
          />
          
          <InputField
            label="Border Radius"
            value={content?.borderRadius || '0.375rem'}
            onChange={(e) => updateContent('borderRadius', e.target.value)}
            placeholder="e.g., 0.375rem, 6px, 0"
            description="Roundness of form elements (0 = square, larger = more rounded)"
          />
          
          <CheckboxField
            label="Rounded Input Fields"
            checked={content?.roundedInput !== false}
            onChange={(e) => updateContent('roundedInput', e.target.checked)}
            description="Apply rounded corners to email input field"
          />
        </div>
      </FormSection>
    </div>
  );

  const renderSpacingTab = () => (
    <div className="space-y-6">
      <FormSection 
        title="Section Spacing" 
        description="Control the space around the newsletter section"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <InputField
            label="Top Padding"
            value={content?.paddingTop || '4rem'}
            onChange={(e) => updateContent('paddingTop', e.target.value)}
            placeholder="e.g., 4rem, 64px"
            description="Space above the section"
          />
          
          <InputField
            label="Bottom Padding"
            value={content?.paddingBottom || '4rem'}
            onChange={(e) => updateContent('paddingBottom', e.target.value)}
            placeholder="e.g., 4rem, 64px"
            description="Space below the section"
          />
          
          <InputField
            label="Bottom Margin"
            value={content?.marginBottom || '3rem'}
            onChange={(e) => updateContent('marginBottom', e.target.value)}
            placeholder="e.g., 3rem, 48px"
            description="External space after section"
          />
        </div>
      </FormSection>

      <FormSection 
        title="Animation Settings" 
        description="Add smooth animations and transitions"
      >
        <CheckboxField
          label="Enable Animations"
          checked={content?.animationEnabled !== false}
          onChange={(e) => updateContent('animationEnabled', e.target.checked)}
          description="Add fade-in and slide animations when the section comes into view"
        />
      </FormSection>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <div className="bg-blue-100 p-2 rounded-lg mr-3">
            <Eye className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h3 className="text-2xl font-bold text-gray-900">Newsletter Section</h3>
            <p className="text-gray-600">Customize your email subscription form</p>
          </div>
        </div>
        
        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-2 p-1 bg-gray-100 rounded-lg">
          {tabs.map((tab) => (
            <TabButton
              key={tab.id}
              tab={tab}
              isActive={activeTab === tab.id}
              onClick={setActiveTab}
            />
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="transition-all duration-300">
        {activeTab === 'content' && renderContentTab()}
        {activeTab === 'design' && renderDesignTab()}
        {activeTab === 'layout' && renderLayoutTab()}
        {activeTab === 'spacing' && renderSpacingTab()}
      </div>
    </div>
  );
};

export default NewsletterForm;



// // src/components/admin/page-management/section-forms/NewsletterForm.js
// import React, { useState } from 'react';
// import ColorPicker from '../../shared/ColorPicker';
// import MediaUploader from '../../shared/MediaUploader';
// import { ChevronDown, ChevronUp } from 'lucide-react';

// const NewsletterForm = ({ content, updateContent }) => {
//   const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  
//   return (
//     <div className="space-y-6">
//       <h3 className="text-lg font-semibold">Newsletter Section</h3>
      
//       {/* Content Settings */}
//       <div className="bg-gray-50 p-4 rounded-md">
//         <h4 className="font-medium mb-4">Content</h4>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Title</label>
//           <input
//             type="text"
//             value={content?.title || 'Join Our Newsletter'}
//             onChange={(e) => updateContent('title', e.target.value)}
//             className="w-full p-2 border rounded"
//           />
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Subtitle</label>
//           <textarea
//             value={content?.subtitle || 'Stay updated with our latest products, promotions, and packaging tips.'}
//             onChange={(e) => updateContent('subtitle', e.target.value)}
//             className="w-full p-2 border rounded"
//             rows="2"
//           />
//         </div>
        
//         <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
//           <div>
//             <label className="block text-sm font-medium mb-1">Button Text</label>
//             <input
//               type="text"
//               value={content?.buttonText || 'Subscribe'}
//               onChange={(e) => updateContent('buttonText', e.target.value)}
//               className="w-full p-2 border rounded"
//             />
//           </div>
//           <div>
//             <label className="block text-sm font-medium mb-1">Input Placeholder</label>
//             <input
//               type="text"
//               value={content?.placeholderText || 'Enter your email'}
//               onChange={(e) => updateContent('placeholderText', e.target.value)}
//               className="w-full p-2 border rounded"
//             />
//           </div>
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Success Message</label>
//           <input
//             type="text"
//             value={content?.successMessage || 'Thank you for subscribing!'}
//             onChange={(e) => updateContent('successMessage', e.target.value)}
//             className="w-full p-2 border rounded"
//           />
//         </div>
        
//         <div className="mb-4">
//           <label className="flex items-center">
//             <input
//               type="checkbox"
//               checked={content?.showPrivacyText !== false}
//               onChange={(e) => updateContent('showPrivacyText', e.target.checked)}
//               className="mr-2"
//             />
//             <span className="text-sm">Show Privacy Text</span>
//           </label>
//         </div>
        
//         {content?.showPrivacyText !== false && (
//           <div className="mb-4">
//             <label className="block text-sm font-medium mb-1">Privacy Text</label>
//             <textarea
//               value={content?.privacyText || 'We respect your privacy and will never share your information.'}
//               onChange={(e) => updateContent('privacyText', e.target.value)}
//               className="w-full p-2 border rounded"
//               rows="2"
//             />
//           </div>
//         )}
//       </div>
      
//       {/* Styling Settings */}
//       <div className="bg-gray-50 p-4 rounded-md">
//         <h4 className="font-medium mb-4">Styling</h4>
        
//         <div className="mb-4">
//           <label className="flex items-center">
//             <input
//               type="checkbox"
//               checked={content?.hasBackgroundImage || false}
//               onChange={(e) => updateContent('hasBackgroundImage', e.target.checked)}
//               className="mr-2"
//             />
//             <span className="text-sm">Use Background Image</span>
//           </label>
//         </div>
        
//         {content?.hasBackgroundImage && (
//           <div className="mb-4">
//             <label className="block text-sm font-medium mb-1">Background Image</label>
//             <MediaUploader
//               type="image"
//               value={content?.backgroundImageUrl || ''}
//               onUploadComplete={(url) => updateContent('backgroundImageUrl', url)}
//               onRemove={() => updateContent('backgroundImageUrl', '')}
//               className="mb-2"
//             />
            
//             <div className="mt-2">
//               <label className="flex items-center">
//                 <input
//                   type="checkbox"
//                   checked={content?.overlayEnabled || false}
//                   onChange={(e) => updateContent('overlayEnabled', e.target.checked)}
//                   className="mr-2"
//                 />
//                 <span className="text-sm">Add Color Overlay</span>
//               </label>
//             </div>
            
//             {content?.overlayEnabled && (
//               <div className="mt-2">
//                 <label className="block text-sm font-medium mb-1">Overlay Color</label>
//                 <ColorPicker
//                   color={content?.overlayColor || 'rgba(0,0,0,0.5)'}
//                   onChange={(color) => updateContent('overlayColor', color)}
//                 />
//               </div>
//             )}
//           </div>
//         )}
        
//         {!content?.hasBackgroundImage && (
//           <>
//             <div className="mb-4">
//               <label className="flex items-center">
//                 <input
//                   type="checkbox"
//                   checked={content?.backgroundGradient || false}
//                   onChange={(e) => updateContent('backgroundGradient', e.target.checked)}
//                   className="mr-2"
//                 />
//                 <span className="text-sm">Use Gradient Background</span>
//               </label>
//             </div>
            
//             {content?.backgroundGradient ? (
//               <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
//                 <div>
//                   <label className="block text-sm font-medium mb-1">Gradient Start Color</label>
//                   <ColorPicker
//                     color={content?.gradientFrom || '#f9fafb'}
//                     onChange={(color) => updateContent('gradientFrom', color)}
//                   />
//                 </div>
//                 <div>
//                   <label className="block text-sm font-medium mb-1">Gradient End Color</label>
//                   <ColorPicker
//                     color={content?.gradientTo || '#f3f4f6'}
//                     onChange={(color) => updateContent('gradientTo', color)}
//                   />
//                 </div>
//               </div>
//             ) : (
//               <div className="mb-4">
//                 <label className="block text-sm font-medium mb-1">Background Color</label>
//                 <ColorPicker
//                   color={content?.backgroundColor || '#f3f4f6'}
//                   onChange={(color) => updateContent('backgroundColor', color)}
//                 />
//               </div>
//             )}
//           </>
//         )}
        
//         <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
//           <div>
//             <label className="block text-sm font-medium mb-1">Text Color</label>
//             <ColorPicker
//               color={content?.textColor || '#111827'}
//               onChange={(color) => updateContent('textColor', color)}
//             />
//           </div>
//           <div>
//             <label className="block text-sm font-medium mb-1">Subtitle Color</label>
//             <ColorPicker
//               color={content?.subtitleColor || '#4b5563'}
//               onChange={(color) => updateContent('subtitleColor', color)}
//             />
//           </div>
//         </div>
        
//         <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
//           <div>
//             <label className="block text-sm font-medium mb-1">Button Color</label>
//             <ColorPicker
//               color={content?.buttonColor || '#013263'}
//               onChange={(color) => updateContent('buttonColor', color)}
//             />
//           </div>
//           <div>
//             <label className="block text-sm font-medium mb-1">Button Text Color</label>
//             <ColorPicker
//               color={content?.buttonTextColor || '#ffffff'}
//               onChange={(color) => updateContent('buttonTextColor', color)}
//             />
//           </div>
//         </div>
        
//         <button
//           type="button"
//           className="mb-4 text-blue-600 hover:text-blue-800 font-medium flex items-center"
//           onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
//         >
//           {showAdvancedSettings ? (
//             <>
//               <ChevronUp className="h-5 w-5 mr-1" />
//               Hide Advanced Settings
//             </>
//           ) : (
//             <>
//               <ChevronDown className="h-5 w-5 mr-1" />
//               Show Advanced Settings
//             </>
//           )}
//         </button>
        
//         {showAdvancedSettings && (
//           <div className="space-y-4">
//             <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
//               <div>
//                 <label className="block text-sm font-medium mb-1">Input Background</label>
//                 <ColorPicker
//                   color={content?.inputBackgroundColor || '#ffffff'}
//                   onChange={(color) => updateContent('inputBackgroundColor', color)}
//                 />
//               </div>
//               <div>
//                 <label className="block text-sm font-medium mb-1">Input Text</label>
//                 <ColorPicker
//                   color={content?.inputTextColor || '#1f2937'}
//                   onChange={(color) => updateContent('inputTextColor', color)}
//                 />
//               </div>
//               <div>
//                 <label className="block text-sm font-medium mb-1">Input Border</label>
//                 <ColorPicker
//                   color={content?.inputBorderColor || '#d1d5db'}
//                   onChange={(color) => updateContent('inputBorderColor', color)}
//                 />
//               </div>
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Border Radius</label>
//               <input
//                 type="text"
//                 value={content?.borderRadius || '0.375rem'}
//                 onChange={(e) => updateContent('borderRadius', e.target.value)}
//                 className="w-full p-2 border rounded"
//                 placeholder="e.g., 0.375rem, 6px"
//               />
//             </div>
            
//             <div className="flex items-center mb-2">
//               <input
//                 type="checkbox"
//                 id="rounded-input"
//                 checked={content?.roundedInput !== false}
//                 onChange={(e) => updateContent('roundedInput', e.target.checked)}
//                 className="mr-2"
//               />
//               <label htmlFor="rounded-input" className="text-sm">Rounded Input Field</label>
//             </div>
            
//             <div className="flex items-center mb-2">
//               <input
//                 type="checkbox"
//                 id="show-icon"
//                 checked={content?.showIcon !== false}
//                 onChange={(e) => updateContent('showIcon', e.target.checked)}
//                 className="mr-2"
//               />
//               <label htmlFor="show-icon" className="text-sm">Show Email Icon</label>
//             </div>
            
//             {content?.showIcon !== false && (
//               <div className="mb-4">
//                 <label className="block text-sm font-medium mb-1">Icon Color</label>
//                 <ColorPicker
//                   color={content?.iconColor || '#374151'}
//                   onChange={(color) => updateContent('iconColor', color)}
//                 />
//               </div>
//             )}
            
//             <div className="flex items-center mb-2">
//               <input
//                 type="checkbox"
//                 id="button-hover"
//                 checked={content?.buttonHoverEffect !== false}
//                 onChange={(e) => updateContent('buttonHoverEffect', e.target.checked)}
//                 className="mr-2"
//               />
//               <label htmlFor="button-hover" className="text-sm">Button Hover Effect</label>
//             </div>
            
//             <div className="flex items-center mb-2">
//               <input
//                 type="checkbox"
//                 id="box-shadow"
//                 checked={content?.boxShadow || false}
//                 onChange={(e) => updateContent('boxShadow', e.target.checked)}
//                 className="mr-2"
//               />
//               <label htmlFor="box-shadow" className="text-sm">Add Box Shadow</label>
//             </div>
            
//             <div className="flex items-center mb-2">
//               <input
//                 type="checkbox"
//                 id="show-divider"
//                 checked={content?.showDivider !== false}
//                 onChange={(e) => updateContent('showDivider', e.target.checked)}
//                 className="mr-2"
//               />
//               <label htmlFor="show-divider" className="text-sm">Show Divider Below Title</label>
//             </div>
            
//             {content?.showDivider !== false && (
//               <div className="mb-4">
//                 <label className="block text-sm font-medium mb-1">Divider Color</label>
//                 <ColorPicker
//                   color={content?.dividerColor || '#e5e7eb'}
//                   onChange={(color) => updateContent('dividerColor', color)}
//                 />
//               </div>
//             )}
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Maximum Width</label>
//               <input
//                 type="text"
//                 value={content?.maxWidth || '36rem'}
//                 onChange={(e) => updateContent('maxWidth', e.target.value)}
//                 className="w-full p-2 border rounded"
//                 placeholder="e.g., 36rem, 576px"
//               />
//               <p className="text-xs text-gray-500 mt-1">
//                 Maximum width of the subscription form
//               </p>
//             </div>
//           </div>
//         )}
//       </div>
      
//       {/* Spacing & Animation */}
//       <div className="bg-gray-50 p-4 rounded-md">
//         <h4 className="font-medium mb-4">Spacing & Animation</h4>
        
//         <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
//           <div>
//             <label className="block text-sm font-medium mb-1">Top Padding</label>
//             <input
//               type="text"
//               value={content?.paddingTop || '4rem'}
//               onChange={(e) => updateContent('paddingTop', e.target.value)}
//               className="w-full p-2 border rounded"
//               placeholder="e.g., 4rem, 64px"
//             />
//           </div>
//           <div>
//             <label className="block text-sm font-medium mb-1">Bottom Padding</label>
//             <input
//               type="text"
//               value={content?.paddingBottom || '4rem'}
//               onChange={(e) => updateContent('paddingBottom', e.target.value)}
//               className="w-full p-2 border rounded"
//               placeholder="e.g., 4rem, 64px"
//             />
//           </div>
//           <div>
//             <label className="block text-sm font-medium mb-1">Bottom Margin</label>
//             <input
//               type="text"
//               value={content?.marginBottom || '3rem'}
//               onChange={(e) => updateContent('marginBottom', e.target.value)}
//               className="w-full p-2 border rounded"
//               placeholder="e.g., 3rem, 48px"
//             />
//           </div>
//         </div>
        
//         <div className="flex items-center mb-2">
//           <input
//             type="checkbox"
//             id="animation-enabled"
//             checked={content?.animationEnabled !== false}
//             onChange={(e) => updateContent('animationEnabled', e.target.checked)}
//             className="mr-2"
//           />
//           <label htmlFor="animation-enabled" className="text-sm">Enable Animation</label>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default NewsletterForm;